import { cn } from '@/lib/utils';
import { useState } from 'react';
import type { TopicNode as TopicNodeModel } from '../types';

interface Props {
  node: TopicNodeModel;
  depth: number;
  selectedPath: string | null;
  onSelect: (path: string) => void;
  /** While a search is active every surviving branch is expanded — the point
   *  of the result set is that the hits are visible without hunting. */
  forceOpen?: boolean;
}

/** One row in the topic tree. Branches expand/collapse; leaves select. */
export function TopicNode({ node, depth, selectedPath, onSelect, forceOpen = false }: Props) {
  const [localOpen, setOpen] = useState(depth < 3);
  const open = forceOpen || localOpen;
  const selected = node.leaf && node.path === selectedPath;

  return (
    <div>
      <button
        type="button"
        // The tree is narrow and truncates names: `waypoint_pred` and
        // `waypoint_approached` both read "waypoint_…". Hover shows the full topic.
        title={node.path}
        onClick={() => (node.leaf ? onSelect(node.path) : setOpen((o) => !o))}
        style={{ paddingLeft: depth * 14 + 8 }}
        className={cn(
          'flex w-full items-center gap-1 py-[3px] pr-2 font-mono text-[0.846em] text-left',
          'text-(--color-text2) hover:text-(--color-text) cursor-pointer select-none',
          // Subscribed-but-silent topic: dim so live topics stand out.
          node.empty && !selected && 'opacity-45',
          selected && 'bg-(--color-green-bg) text-(--color-green) rounded',
        )}
      >
        <span
          className={cn(
            'w-3.5 shrink-0 text-(--color-text3) transition-transform',
            open && !node.leaf && 'rotate-90',
          )}
        >
          {node.leaf ? '' : '▸'}
        </span>
        <span className={cn('flex-1 truncate', selected && 'font-bold')}>{node.name}</span>
        {node.live && (
          <span className="w-[5px] h-[5px] rounded-full bg-(--color-green) shrink-0 animate-[pulse-dot_2s_ease-in-out_infinite]" />
        )}
        {node.count > 0 ? (
          <span className="font-sans text-[0.846em] text-(--color-text3) shrink-0">
            {node.count.toLocaleString()}
          </span>
        ) : (
          node.leaf && (
            <span className="font-sans text-[0.769em] italic text-(--color-text3) shrink-0">
              empty
            </span>
          )
        )}
      </button>
      {!node.leaf &&
        open &&
        node.children.map((c) => (
          <TopicNode
            key={c.path}
            node={c}
            depth={depth + 1}
            selectedPath={selectedPath}
            onSelect={onSelect}
            forceOpen={forceOpen}
          />
        ))}
    </div>
  );
}
